import React, { useContext, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';

// Module-level animation state tracking
const cardAnimationState = new Map();

// Card component moved outside to prevent re-creation
const Card = ({ children, className = '', delay = 0, cardId }) => {
  const hasAnimated = cardAnimationState.has(cardId) ? cardAnimationState.get(cardId) : false;

  React.useEffect(() => {
    if (!hasAnimated) {
      cardAnimationState.set(cardId, true);
    }
  }, [cardId, hasAnimated]);

  return (
    <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: hasAnimated ? 0 : delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 md:p-8 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const AboutMetyPage = () => {
  const { isDarkMode } = useContext(ThemeContext);

  // Memoize title gradient to update when dark mode changes
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);


  return (
    <div className={`min-h-screen pt-20 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 md:mb-12"
        >
          <h1
            key={`about-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-6 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            About METY Technology
          </h1>
          {/* METY Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mb-8"
          >
            <img
              src="/img/mety-logo.svg"
              alt="METY Technology Logo"
              className="mx-auto max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg h-auto"
            />
          </motion.div>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
          {/* Main Content */}
          <Card delay={0.2} cardId="about-main-content">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <p className="text-base md:text-lg lg:text-xl text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left mb-8">
                METY Technology is an innovative software company dedicated to using Artificial Intelligence (AI) 
                and Machine Learning (ML) to address significant societal challenges and improve human well-being—their 
                mission centers on developing personalized, data-driven solutions for education, health, and wellness.
              </p>

              <motion.a
                href="https://metytechnology.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block py-4 px-8 bg-gradient-to-r from-[#06bae4] to-[#06bae4] hover:from-[#05a5d1] hover:to-[#05a5d1] text-white font-bold font-zonaProUltra rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-base md:text-lg mb-6"
              >
                Learn More
              </motion.a>

              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular">
                To explore METY Technology's mission, team, and the full range of features offered by their breakthrough platforms, visit us at METY Technology.
              </p>
            </motion.div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutMetyPage;

